import React, { useState, useEffect } from 'react';
import { 
  Card,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { dashboardAPI, categoryAPI } from '../api/api';
import { getColorForChartType } from '../utils/colorUtils';

const MonthlyUsageByCategoryChart = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Store all categories for mapping
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Get both dashboard categories and all categories for standard name mapping
        const [dashboardResponse, allCategoriesResponse] = await Promise.all([
          dashboardAPI.getCategoryDistribution(),
          categoryAPI.getCategories()
        ]);
        
        setAllCategories(allCategoriesResponse.data || []);
        
        // Map the dashboard categories to use standard names if available
        const categoryList = (dashboardResponse.data || []).map(item => {
          const category = (allCategoriesResponse.data || []).find(c => 
            c.name.toLowerCase() === (item.name || '').toLowerCase()
          );
          
          // Use standard name if available, otherwise use the original name
          const displayName = category && category.standard_name && category.standard_name.trim() !== '' 
            ? category.standard_name 
            : item.name || 'Unknown';
          
          return {
            id: item.id || item.category_id,
            originalName: item.name || 'Unknown', // Keep original name for filtering
            name: displayName
          };
        });
        
        setCategories(categoryList);
        
        // Set the first category as default if available
        if (categoryList.length > 0) {
          setSelectedCategory(categoryList[0].originalName); // Use original name for filtering
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      const fetchCategoryData = async () => {
        setLoading(true);
        try {
          // For this implementation, we'll call the getCategoryWiseData API
          // Since this API might not be category-specific in the backend, 
          // we'll need to filter the data on the frontend if needed
          const response = await dashboardAPI.getCategoryWiseData('monthly');
          
          // Filter data for the selected category using original name
          const filteredData = (response.data || []).filter(item => 
            (item.category_name || item.name || '').toLowerCase() === selectedCategory.toLowerCase()
          );
          
          // Format the period for better display
          const formattedData = filteredData.map(item => {
            // Map the category name in the chart data to use standard name if available
            const category = allCategories.find(c => 
              c.name.toLowerCase() === (item.category_name || item.name || '').toLowerCase()
            );
            
            const displayName = category && category.standard_name && category.standard_name.trim() !== '' 
              ? category.standard_name 
              : item.category_name || item.name || 'Unknown';
            
            return {
              ...item,
              category_name: displayName, // Update the name to use standard name
              month: new Date(item.period || item.month).toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric'
              })
            };
          });
          
          setChartData(formattedData);
        } catch (error) {
          console.error('Error fetching category-wise data:', error);
          setChartData([]);
        } finally {
          setLoading(false);
        }
      };

      fetchCategoryData();
    }
  }, [selectedCategory, allCategories]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div 
          style={{
            backgroundColor: '#fff',
            padding: '10px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          <p style={{ margin: 0, fontWeight: 600 }}>{label}</p>
          <p style={{ margin: '5px 0 0', color: '#3f51b5' }}>
            ${payload[0].value.toFixed(2)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card 
      elevation={0} 
      sx={{ 
        p: 3, 
        backgroundColor: 'background.paper',
        borderRadius: 3,
        height: '100%',
        boxShadow: 1
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Monthly Usage - Category
        </Typography>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map((category, index) => (
              <MenuItem key={index} value={category.originalName}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ height: 350 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 50, // Increased bottom margin for rotated labels
              }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
              <XAxis 
                dataKey="month" 
                angle={-45} 
                textAnchor="end"
                height={50}
                tick={{ fill: '#666', fontSize: 11 }}
              />
              <YAxis 
                tick={{ fill: '#666' }}
                tickFormatter={(value) => `${value}`}
              />
              <Tooltip 
                content={<CustomTooltip />} 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              {/* Remove legend for single-value charts */}
              <Bar 
                dataKey="total_amount" 
                name="Amount" 
                fill={getColorForChartType('Monthly Usage', 'bar')} 
                radius={[4, 4, 0, 0]} // Rounded corners for bars
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              {selectedCategory ? 'No data available for selected category' : 'Select a category to view data'}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default MonthlyUsageByCategoryChart;